import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import StoryCard from '../components/StoryCard'
import QuizCard from '../components/QuizCard'
import { useAuth } from '../context/AuthContext'
import SEO from '../components/SEO'

export default function DraftsPage() {
  const { user, getToken } = useAuth()
  const [storyDrafts, setStoryDrafts] = useState([])
  const [quizDrafts, setQuizDrafts] = useState([])
  const [loading, setLoading] = useState(true)

  const currentUserId = user?.id

  useEffect(() => {
    async function fetch() {
      try {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const [storiesRes, quizzesRes] = await Promise.all([
          axios.get('/api/stories/my-drafts', { headers }),
          axios.get('/api/quizzes/mine', { headers }),
        ])
        setStoryDrafts(storiesRes.data)
        setQuizDrafts((quizzesRes.data ?? []).filter(q => !q.published))
      } catch (err) { console.error('[DraftsPage] Failed to fetch drafts:', err) }
      setLoading(false)
    }
    fetch()
  }, [getToken])

  async function handleDeleteStory(id) {
    if (!window.confirm('Delete this draft?')) return
    try {
      const token = await getToken()
      await axios.delete(`/api/stories/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      setStoryDrafts(prev => prev.filter(s => s.id !== id))
    } catch (err) { console.error('[DraftsPage] Failed to delete story draft:', err) }
  }

  async function handleDeleteQuiz(id) {
    if (!window.confirm('Delete this quiz draft?')) return
    const token = await getToken()
    await axios.delete(`/api/quizzes/${id}`, { headers: { Authorization: `Bearer ${token}` } })
    setQuizDrafts(prev => prev.filter(q => q.id !== id))
  }

  const totalDrafts = storyDrafts.length + quizDrafts.length

  return (
    <>
      <Navbar />
      <SEO title="My Drafts" description="Your unpublished story and quiz drafts on Verse." noindex />
      <div className="drafts-page">
        <div className="drafts-page__header">
          <h1 className="drafts-page__title">My Drafts</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/write" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
              ✍️ New story
            </Link>
            <Link to="/quiz/create" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
              🧩 New quiz
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="drafts-page__loading">Loading drafts…</div>
        ) : totalDrafts === 0 ? (
          <div className="profile-empty">
            <div className="profile-empty__icon">📝</div>
            <p className="profile-empty__title">No drafts</p>
            <p className="profile-empty__desc">
              Drafts you save while writing will appear here.
            </p>
            <Link to="/write" className="btn btn-primary" style={{ marginTop: 16 }}>
              Start writing
            </Link>
          </div>
        ) : (
          <>
            <p className="drafts-page__count">
              {totalDrafts} {totalDrafts === 1 ? 'draft' : 'drafts'}
            </p>

            {storyDrafts.length > 0 && (
              <>
                {quizDrafts.length > 0 && <h2 className="drafts-page__section-title">Stories</h2>}
                <div className="story-grid">
                  {storyDrafts.map((story, i) => (
                    <StoryCard
                      key={story.id}
                      story={story}
                      index={i}
                      onDelete={handleDeleteStory}
                    />
                  ))}
                </div>
              </>
            )}

            {quizDrafts.length > 0 && (
              <>
                {storyDrafts.length > 0 && <h2 className="drafts-page__section-title">Quizzes</h2>}
                <div className="story-grid">
                  {quizDrafts.map((quiz, i) => (
                    <QuizCard
                      key={quiz.id}
                      quiz={quiz}
                      index={i}
                      currentUserId={currentUserId}
                      onDelete={handleDeleteQuiz}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}
